import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import Templates from '../templates/templates';
import CustomAnswerNextMapField from './CustomAnswerNextMapField';

const DRAFT_KEY = 'createDeploymentDraft'; // must match CreateDeployment

//Question tagging feature
const ALLOWED_TAGS = [
  'demographic',
  'historical_ranking',
  'pairwise_comparison',
  'baseline_practices',
  'current_season_monitoring',
  'forecast_validation',
  'post_harvest_assessment',
  'meta'
];

const functionNames = [
  'get_current_date','get_from_response','get_score_consensus','get_score_right_answer','get_unique_matching_responses','get_pairwise_comparison_string_option','get_percentage_neighbors','get_noki_final','get_region_from_municipality','get_score_dynamic_enroll','get_score','get_next_comparison','get_number_of_comparisons_done','get_number_of_pairwise_comparisons'
];

const functionDescription = {
  get_current_date: 'get the current date; 1 arg; date',
  get_from_response: 'get variable from response; 1 arg; step number from wherer to fetch',
  get_unique_matching_responses: 'get all the unique matching responses; 0 arg',
  get_score_consensus: 'scoring_method : string , options: Consensus, points_rewarded_correct : integer points_rewarded_wrong : integer',
  get_score_right_answer: 'scoring_method : string , options: Right answer, the right answer : string,  points_rewarded_correct : integer points_rewarded_wrong : integer',
  get_score_dynamic_enroll: 'Arg1 (scoring method) : string , Options : dynamic_score_enroll, DATABASE.  Arg2 (usecase) : string. Arg3 (source) : string. Arg4 (points_rewarded_correct) : integer. Arg5 (points_rewarded_wrong) : integer',
  get_pairwise_comparison_string_option: 'Arguments required: Arg 0 = use case name, Arg 1 = maximum amount of comparisons for user, Arg 2 = 1 or 2 (to return comparison object 1 or 2)',
  get_percentage_neighbors: 'get the percentage of neighbors who matched the user\'s answer; 0 arg',
  get_noki_final: 'get the final score; 0 arg',
  get_region_from_municipality: 'Lets user select a municipality and assigns a region in the backend; 2 args; Arg 0 = Municipality, Arg 1 = Region',
  get_next_comparison: 'get the next step; 1 arg; number of times to repeat the steps',
  get_number_of_comparisons_done: 'get the number of comparisons done; 0 arg',
  get_number_of_pairwise_comparisons: 'get the number of pairwise comparisons; 0 arg',
  get_score: 'Arg1 (scoring method) : string , Options : Consensus, RightAnswer, dynamic_score_enroll, DATABASE.  Arg2 (usecase: if dynamic_score_enroll or DATABASE) : string. Arg3 (source: if dynamic_score_enroll or DATABASE) : string. Arg4 (points_rewarded_correct) : integer. Arg5 (points_rewarded_wrong) : integer',
};

const initialFormData = {
  survey_name: 'demo_survey',
  restart_word: 'restart',
  start_date: 'YYYY-MM-DD',
  end_date: 'YYYY-MM-DD',
  survey_inactive_message: 'inactive survey response',
  survey: {
    start: {
      type: 'var',
      msgs: ['Hi! This is the start of the survey'],
      next: 'start'
    },
  }
};

const uiSchema = {
  "ui:globalOptions": { enableMarkdownInDescription: true, orderable: false },
  "ui:submitButtonOptions": { submitText: "Generate JSON", norender: false },
  start_date: { "ui:widget": "date" },
  end_date: { "ui:widget": "date" },
  survey: {
    additionalProperties: {
      tags: { "ui:widget": "select" },
      next_condition: {
        "ui:options": { label: false },
        answer_next_map: { "ui:field": "CustomAnswerNextMapField" }
      }
    }
  }
};

const baseSchema = {
  title: 'NOKi Form',
  type: 'object',
  required: ['restart_word', 'survey_name'],
  properties: {
    survey_name: { type: 'string', title: 'Survey Name', description: 'This is what the survey will be saved as.' },
    restart_word: { type: 'string', title: 'Restart Message', description: 'This message will restart this survey process.' },
    start_date: { type: 'string', format: 'date', title: 'Start Date (Optional)', description: 'The date when the survey becomes active. Must be declared if End Date is provided.' },
    end_date: { type: 'string', format: 'date', title: 'End Date (Optional)', description: 'The date when the survey becomes inactive. This is optional.' },
    survey_inactive_message: {
      type: 'string',
      title: 'Inactive Survey Response (Optional)',
      description: 'This message will be sent to the user if they try to start an inactive survey. Must be declared if Start Date is provided.'
    },
    survey: {
      type: 'object',
      title: 'Survey Questions',
      description: "Configure the survey questions with various types and messages.",
      additionalProperties: {
        type: 'object',
        properties: {
          tags: { type: 'string',title: 'Question Category',enum: ALLOWED_TAGS, default: '',description: 'Choose a category that describes what this question measures or relates to.'  },
          type: { type: 'string', title: 'Question Type', description: 'Choose the type of question you want to ask.', enum: ['var', 'text', 'mcq'] },
          msgs: { type: 'array', title: 'Message', items: { type: 'string' } },
          params: {
            type: 'object',
            title: 'Message Variable (if Question Type is text)',
            additionalProperties: {
              type: 'object',
              properties: {
                function_name: { type: 'string', enum: functionNames, title: 'Function Name' },
                args: {
                  type: 'array',
                  items: {
                    oneOf: [
                      { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                      { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' },
                      { type: 'boolean', title: 'Boolean Value', enum: [true, false] }
                    ]
                  }
                }
              },
              allOf: [
                { if: { properties: { function_name: { const: 'get_current_date' } } }, then: { properties: { function_name: { description: functionDescription.get_current_date } } } },
                { if: { properties: { function_name: { const: 'get_score' } } }, then: { properties: { function_name: { description: functionDescription.get_score } } } },
                { if: { properties: { function_name: { const: 'get_from_response' } } }, then: { properties: { function_name: { description: functionDescription.get_from_response } } } },
                { if: { properties: { function_name: { const: 'get_score_consensus' } } }, then: { properties: { function_name: { description: functionDescription.get_score_consensus } } } },
                { if: { properties: { function_name: { const: 'get_score_right_answer' } } }, then: { properties: { function_name: { description: functionDescription.get_score_right_answer } } } },
                { if: { properties: { function_name: { const: 'get_pairwise_comparison_string_option' } } }, then: { properties: { function_name: { description: functionDescription.get_pairwise_comparison_string_option } } } },
                { if: { properties: { function_name: { const: 'get_unique_matching_responses' } } }, then: { properties: { function_name: { description: functionDescription.get_unique_matching_responses } } } },
                { if: { properties: { function_name: { const: 'get_next_comparison' } } }, then: { properties: { function_name: { description: functionDescription.get_next_comparison } } } },
                { if: { properties: { function_name: { const: 'get_number_of_comparisons_done' } } }, then: { properties: { function_name: { description: functionDescription.get_number_of_comparisons_done } } } },
                { if: { properties: { function_name: { const: 'get_number_of_pairwise_comparisons' } } }, then: { properties: { function_name: { description: functionDescription.get_number_of_pairwise_comparisons } } } },
              ]
            }
          },
          options: { type: "object", title: 'MCQ Options (if Question Type is mcq)', additionalProperties: { type: "string" } },
          next: { type: 'string', title: 'Next Question', description: 'The next question to move to. Leave blank if you want to set the next question conditionally or if you want to end the survey.', enum: [''] },
          next_condition: {
            type: 'object',
            properties: {
              function_name: { type: 'string', enum: functionNames, title: 'Next Condition Function Name', description: 'The function used to determine the next question to move to.' },
              args: {
                type: 'array',
                items: {
                  oneOf: [
                    { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                    { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' }
                  ]
                }
              },
              answer_next_map: {
                type: 'object',
                title: 'Answer to Next Question Mapping (if Next Condition Function Name is set)',
                description: 'Map a user\'s answer to the next question to move to.',
                additionalProperties: { type: 'string' }
              }
            }
          }
        },
        allOf: [
          {
            if: { properties: { type: { const: "var" } } },
            then: {
              properties: {
                msgs: { type: 'array', title: 'Message', items: { type: 'string' } },
                params: {
                  type: 'object',
                  title: 'Message Variable (if Question Type is var)',
                  additionalProperties: {
                    type: 'object',
                    properties: {
                      function_name: { type: 'string', enum: functionNames, title: 'Function Name' },
                      args: {
                        type: 'array',
                        items: {
                          oneOf: [
                            { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                            { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' }
                          ]
                        }
                      }
                    },
                    allOf: [
                      { if: { properties: { function_name: { const: 'get_current_date' } } }, then: { properties: { function_name: { description: functionDescription.get_current_date } } } },
                      { if: { properties: { function_name: { const: 'get_from_response' } } }, then: { properties: { function_name: { description: functionDescription.get_from_response } } } },
                      { if: { properties: { function_name: { const: 'get_score_consensus' } } }, then: { properties: { function_name: { description: functionDescription.get_score_consensus } } } },
                      { if: { properties: { function_name: { const: 'get_score_right_answer' } } }, then: { properties: { function_name: { description: functionDescription.get_score_right_answer } } } },
                      { if: { properties: { function_name: { const: 'get_pairwise_comparison_string_option' } } }, then: { properties: { function_name: { description: functionDescription.get_pairwise_comparison_string_option } } } },
                      { if: { properties: { function_name: { const: 'get_unique_matching_responses' } } }, then: { properties: { function_name: { description: functionDescription.get_unique_matching_responses } } } },
                    ]
                  }
                }
              }
            }
          },
          {
            if: { properties: { type: { const: "text" } } },
            then: {
              properties: {
                msgs: { type: 'array', title: 'Message', items: { type: 'string' } },
                params: {
                  type: 'object',
                  title: 'Message Variable (if Question Type is var)',
                  additionalProperties: {
                    type: 'object',
                    properties: {
                      function_name: { type: 'string', enum: functionNames, title: 'Function Name' },
                      args: {
                        type: 'array',
                        items: {
                          oneOf: [
                            { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                            { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' },
                            { type: 'boolean', title: 'Boolean Value', enum: [true, false] }
                          ]
                        }
                      }
                    },
                    allOf: [
                      { if: { properties: { function_name: { const: 'get_current_date' } } }, then: { properties: { function_name: { description: functionDescription.get_current_date } } } },
                      { if: { properties: { function_name: { const: 'get_from_response' } } }, then: { properties: { function_name: { description: functionDescription.get_from_response } } } },
                      { if: { properties: { function_name: { const: 'get_score_consensus' } } }, then: { properties: { function_name: { description: functionDescription.get_score_consensus } } } },
                      { if: { properties: { function_name: { const: 'get_noki_final' } } }, then: { properties: { function_name: { description: functionDescription.get_noki_final } } } },
                      { if: { properties: { function_name: { const: 'get_percentage_neighbors' } } }, then: { properties: { function_name: { description: functionDescription.get_percentage_neighbors } } } },
                      { if: { properties: { function_name: { const: 'get_unique_matching_responses' } } }, then: { properties: { function_name: { description: functionDescription.get_unique_matching_responses } } } },
                    ]
                  }
                }
              }
            }
          },
          {
            if: { properties: { type: { const: "mcq" } } },
            then: {
              properties: {
                msgs: { type: 'array', title: 'Message', items: { type: 'string' } },
                params: {
                  type: 'object',
                  title: 'Message Variable (if Question Type is var)',
                  additionalProperties: {
                    type: 'object',
                    properties: {
                      function_name: { type: 'string', enum: functionNames, title: 'Function Name' },
                      args: {
                        type: 'array',
                        items: {
                          oneOf: [
                            { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                            { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' },
                            { type: 'boolean', title: 'Boolean Value', enum: [true, false] }
                          ]
                        }
                      }
                    },
                    allOf: [
                      { if: { properties: { function_name: { const: 'get_current_date' } } }, then: { properties: { function_name: { description: functionDescription.get_current_date } } } },
                      { if: { properties: { function_name: { const: 'get_from_response' } } }, then: { properties: { function_name: { description: functionDescription.get_from_response } } } },
                      { if: { properties: { function_name: { const: 'get_score_consensus' } } }, then: { properties: { function_name: { description: functionDescription.get_score_consensus } } } },
                      { if: { properties: { function_name: { const: 'get_score_right_answer' } } }, then: { properties: { function_name: { description: functionDescription.get_score_right_answer } } } },
                      { if: { properties: { function_name: { const: 'get_pairwise_comparison_string_option' } } }, then: { properties: { function_name: { description: functionDescription.get_pairwise_comparison_string_option } } } },
                      { if: { properties: { function_name: { const: 'get_region_from_municipality' } } }, then: { properties: { function_name: { description: functionDescription.get_region_from_municipality } } } },
                      { if: { properties: { function_name: { const: 'get_unique_matching_responses' } } }, then: { properties: { function_name: { description: functionDescription.get_unique_matching_responses } } } },
                    ]
                  }
                },
                options: { type: "object", title: 'MCQ Options (if Question Type is mcq)', additionalProperties: { type: "string" } }
              }
            }
          }
        ]
      }
    }
  }
};

const App = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => {
    const savedData = sessionStorage.getItem('savedFormData');
    return savedData ? JSON.parse(savedData) : initialFormData;
  });

  const [submittedData, setSubmittedData] = useState(() => {
    const savedJson = sessionStorage.getItem('submittedData');
    return savedJson ? JSON.parse(savedJson) : null;
  });

  // optional deployment metadata to send along
  const [deployMeta, setDeployMeta] = useState(() => {
    const saved = sessionStorage.getItem('savedDeployMeta');
    return saved
      ? JSON.parse(saved)
      : { deployment_name: '', instance_name: '', user_start_message: '', comments: '' };
  });

  const [dynamicSchema, setDynamicSchema] = useState(baseSchema);

  // --- AUTOSAVE infra (sessionStorage for this screen) ---
  const saveDebounceRef = useRef(null);
  const AUTOSAVE_DELAY = 300;

  const persistSelf = (data, submitted = submittedData, meta = deployMeta) => {
    try {
      sessionStorage.setItem('savedFormData', JSON.stringify(data));
      if (submitted !== undefined && submitted !== null) {
        sessionStorage.setItem('submittedData', JSON.stringify(submitted));
      }
      sessionStorage.setItem('savedDeployMeta', JSON.stringify(meta));
    } catch (e) {
      console.warn('Autosave failed:', e);
    }
  };

  const autosaveNow = () => {
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    persistSelf(formData, submittedData, deployMeta);
  };

  const scheduleAutosave = () => {
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(
      () => persistSelf(formData, submittedData, deployMeta),
      AUTOSAVE_DELAY
    );
  };

  useEffect(() => {
    scheduleAutosave();
    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, deployMeta, submittedData]);

  useEffect(() => {
    const onBeforeUnload = () => autosaveNow();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Scroll-to-bottom ref =====
  const bottomRef = useRef(null);

  const handleSubmit = ({ formData }) => {
    const ensureArgsInParams = (obj) => {
      if (typeof obj !== 'object' || obj === null) return;
      for (const key in obj) {
        if (key === 'params' && typeof obj[key] === 'object') {
          for (const paramKey in obj[key]) {
            if (obj[key][paramKey].function_name && !('args' in obj[key][paramKey])) {
              obj[key][paramKey].args = [];
            }
            if (obj[key][paramKey].function_name === 'get_score_right_answer') obj[key][paramKey].function_name = 'get_score';
            if (obj[key][paramKey].function_name === 'get_score_consensus') obj[key][paramKey].function_name = 'get_score';
            if (obj[key][paramKey].function_name === 'get_score_dynamic_enroll') obj[key][paramKey].function_name = 'get_score';
          }
        } else if (typeof obj[key] === 'object') {
          ensureArgsInParams(obj[key]);
        }
      }
    };

    ensureArgsInParams(formData);

    const reorderedFormData = {
      restart_word: formData.restart_word,
      survey_inactive_message: formData.survey_inactive_message || null,
      ...Object.fromEntries(Object.entries(formData).filter(
        ([key]) => !["restart_word", "survey_inactive_message"].includes(key)
      ))
    };

    const cleanedFormData = {
      ...reorderedFormData,
      survey: cleanSurveySteps(formData.survey)
    };

    setSubmittedData(cleanedFormData);
    persistSelf(formData, cleanedFormData, deployMeta);

    // >>> Smooth scroll to bottom after "Submitted Data" appears
    setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        // fallback: scroll window
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
    }, 50);
  };

  const extractKeysFromSurvey = (survey) => Object.keys(survey);

  const generateDynamicSchema = () => {
    const surveyKeys = extractKeysFromSurvey(formData.survey);
    const nextEnum = surveyKeys.length ? surveyKeys : [''];
    return {
      ...baseSchema,
      properties: {
        ...baseSchema.properties,
        survey: {
          ...baseSchema.properties.survey,
          additionalProperties: {
            ...baseSchema.properties.survey.additionalProperties,
            properties: {
              ...baseSchema.properties.survey.additionalProperties.properties,
              next: {
                type: 'string',
                title: 'Next Question',
                description: 'The next question to move to. Leave blank if you want to set the next question conditionally or if you want to end the survey.',
                enum: nextEnum
              },
              next_condition: {
                type: 'object',
                properties: {
                  function_name: {
                    type: 'string',
                    enum: functionNames,
                    title: 'Next Condition Function Name',
                    description: 'The function used to determine the next question to move to (Leave blank if Next Question is set).'
                  },
                  args: {
                    type: 'array',
                    items: {
                      oneOf: [
                        { type: 'string', title: 'String Value', pattern: '^[a-zA-Z0-9_]*$' },
                        { type: 'integer', title: 'Integer Value', pattern: '^[0-9]*$' }
                      ]
                    }
                  },
                  answer_next_map: {
                    type: 'object',
                    title: 'Answer to Next Question Mapping (if Next Condition Function Name is set)',
                    description: 'Map a user\'s answer to the next question to move to.',
                    additionalProperties: { type: 'string', enum: nextEnum }
                  }
                }
              }
            }
          }
        }
      }
    };
  };

  useEffect(() => {
    setDynamicSchema(generateDynamicSchema());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = ({ formData }) => {
    const updatedFormData = { ...formData };

    const traverseAndUpdateArgs = (obj) => {
      if (typeof obj !== 'object' || obj === null) return;
      for (const key in obj) {
        if (key === 'params' && typeof obj[key] === 'object') {
          for (const paramKey in obj[key]) {
            const param = obj[key][paramKey];
            if (param.function_name === 'get_score_consensus') {
              param.args = ['Consensus', 'None', param.args?.[2] || 100, param.args?.[3] || 50];
            } else if (param.function_name === 'get_score_right_answer') {
              param.args = ['RightAnswer', param.args?.[1] || 'EnterRightAnswer', param.args?.[2] || 100, param.args?.[3] || 50];
            } else if (param.function_name === 'get_score_dynamic_enroll') {
              param.args = ['dynamic_score_enroll', param.args?.[1] || 'EnterUsecase', param.args?.[2] || 'EnterSource', param.args?.[3] || 100, param.args?.[4] || 50];
            }
          }
        } else if (typeof obj[key] === 'object') {
          traverseAndUpdateArgs(obj[key]);
        }
      }
    };

    traverseAndUpdateArgs(updatedFormData);
    setFormData(updatedFormData);
    scheduleAutosave();
  };

  const handleCopytoClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(submittedData, null, 2));
    alert('Copied to clipboard!');
  };

  const handleSendToCreate = () => {
    if (!submittedData) {
      alert('Click "Generate JSON" first, then try again.');
      return;
    }
    const draft = {
      deployment_name: (deployMeta.deployment_name || formData.survey_name || '').trim(),
      instance_name: (deployMeta.instance_name || '').trim(),
      user_start_message: (deployMeta.user_start_message || '').trim(),
      comments: (deployMeta.comments || '').trim(),
      start_date: formData.start_date && formData.start_date !== 'YYYY-MM-DD' ? formData.start_date : '',
      end_date: formData.end_date && formData.end_date !== 'YYYY-MM-DD' ? formData.end_date : '',
      script: JSON.stringify(submittedData, null, 2),
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {
      console.warn('Failed to stash draft for Create page:', e);
    }
    navigate('/create-survey'); // change if your route differs
  };

  function cleanSurveySteps(survey) {
    const cleaned = {};
    for (const [key, step] of Object.entries(survey)) {
      const newStep = { ...step };
      if ('next' in newStep && (!newStep.next || newStep.next === '')) delete newStep.next;
      if ('next_condition' in newStep) {
        const nc = newStep.next_condition;
        if (nc.answer_next_map && Object.keys(nc.answer_next_map).length === 0) delete nc.answer_next_map;
        if ((!nc.function_name || nc.function_name === '') && (!nc.args || nc.args.length === 0) && (!nc.answer_next_map || Object.keys(nc.answer_next_map).length === 0)) {
          delete newStep.next_condition;
        }
      }
      if ('params' in newStep && Object.keys(newStep.params).length === 0) delete newStep.params;
      if ('options' in newStep && Object.keys(newStep.options).length === 0) delete newStep.options;
      cleaned[key] = newStep;
    }
    return cleaned;
  }

  return (
    <div className='form-container'>
      <div>
        <h3>Import Survey Template</h3>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <textarea
            id="pasted-json"
            rows="10"
            cols="50"
            placeholder="Paste your Survey Config here"
            style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", marginBottom: "5px" }}
          />
          <button
            onClick={() => {
              const textarea = document.getElementById('pasted-json');
              try {
                const pastedData = JSON.parse(textarea.value);
                setFormData(pastedData);
                setSubmittedData(null);
                persistSelf(pastedData, null, deployMeta);
              } catch (error) {
                alert('Invalid JSON format. Please check your input.');
              }
            }}
            style={{ backgroundColor: "#0275d8", color: "white", border: "none", padding: "6px 12px", borderRadius: "5px", cursor: "pointer", fontSize: "14px", marginTop: "5px" }}
          >
            Set Form Data
          </button>
        </div>
      </div>

      <div className='note'>
        <b>NOTE:</b> Please ensure that the survey questions form below begins with the key labeled "start" and ends with the key labeled "end".
      </div>

      {/* Optional deployment metadata */}
      <div style={{ margin: "16px 0", padding: "12px", border: "1px solid #e5e5e5", borderRadius: 8 }}>
        <h4 style={{ marginTop: 0 }}>Deployment Details (optional)</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Deployment Name</span>
            <input
              type="text"
              value={deployMeta.deployment_name}
              onChange={(e) => setDeployMeta(m => ({ ...m, deployment_name: e.target.value }))}
              placeholder="e.g. survey_2025_pilot"
            />
            <small style={{ color: "#666" }}>Defaults to the Survey Name if left blank.</small>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Instance Name</span>
            <input
              type="text"
              value={deployMeta.instance_name}
              onChange={(e) => setDeployMeta(m => ({ ...m, instance_name: e.target.value }))}
              placeholder="e.g. prod / pilot / test"
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>User Start Message</span>
            <input
              type="text"
              value={deployMeta.user_start_message}
              onChange={(e) => setDeployMeta(m => ({ ...m, user_start_message: e.target.value }))}
              placeholder='e.g. "start"'
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>Comments</span>
            <input
              type="text"
              value={deployMeta.comments}
              onChange={(e) => setDeployMeta(m => ({ ...m, comments: e.target.value }))}
              placeholder="Optional notes"
            />
          </label>
        </div>
      </div>

      <Form
        schema={dynamicSchema}
        formData={formData}
        validator={validator}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onError={console.log.bind(console, 'errors')}
        uiSchema={uiSchema}
        fields={{ CustomAnswerNextMapField }}
        onBlur={() => autosaveNow()}
        templates={Templates}
      />

      <br />
      {submittedData && (
        <div>
          <h3>Submitted Data:</h3>
          <pre>{JSON.stringify(submittedData, null, 2)}</pre>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleCopytoClipboard}
              style={{ backgroundColor: "#5bc0de", color: "white", border: "none", padding: "8px 16px", borderRadius: "5px", cursor: "pointer", fontSize: "16px" }}
            >
              Copy JSON
            </button>

            <button
              onClick={handleSendToCreate}
              style={{ backgroundColor: "#6f42c1", color: "white", border: "none", padding: "8px 16px", borderRadius: "5px", cursor: "pointer", fontSize: "16px" }}
            >
              Send to Create Form
            </button>
          </div>

          {/* anchor for smooth scroll */}
          <div ref={bottomRef} style={{ height: 1 }} />
          <br /><br />
        </div>
      )}
    </div>
  );
};

export default App;
